import { useRef } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import I18n, { t } from 'i18n-js';

import { subscribeToNewsletter as subscribeAction } from '@lootboy/lib/actions/UserActions';
import {
  ENewsletterField,
  useNewsletterForm,
} from '@lootboy/lib/forms/useNewsletterForm';
import { emailSelector, newsletterEmailSelector } from '@lootboy/lib/selectors';

import Alert from '~/components/Alert';
import Input from '~/components/atoms/FormControls/Input';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import Image from '~/components/Image';
import Markdown from '~/components/Markdown';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { CaptchaOverlay } from '~/components/molecules/CaptchaOverlay';

import useBooleanState from '~/hooks/useBooleanState';
import { useClosePopup } from '~/hooks/useClosePopup';
import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import { parseLootBoyError } from '~/utilities/errors';

import { ReactComponent as CheckIcon } from '~/assets/images/iconsLibrary/check-circle.svg';

import newsletterHero from './newsletter-hero.png';

import styles from './NewsletterPopup.module.scss';

export function NewsletterPopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const isLoggedIn = useLoggedIn();
  const newsletterEmail = useSelector<TAppStore, string>(
    newsletterEmailSelector,
  );
  const email = useSelector<TAppStore, string>(emailSelector);
  const closePopup = useClosePopup();
  const [isCaptchaShown, { setFalse: hideCaptcha, setTrue: showCaptcha }] =
    useBooleanState();

  const isAlreadySubscribed = useRef(!!newsletterEmail).current;

  const {
    register,
    onFormSubmit,
    getValues,
    formState: { errors, isValid, isSubmitting },
  } = useNewsletterForm(
    () => {
      showCaptcha();
    },
    {
      defaultValues: {
        [ENewsletterField.EMAIL]: newsletterEmail || email || '',
      },
    },
  );

  const subscribeToNewsletter = async (captchaKey: string) => {
    try {
      const formEmail = getValues(ENewsletterField.EMAIL);
      const { alreadySubscribed } = await dispatch(
        subscribeAction(formEmail, captchaKey),
      );
      if (isAlreadySubscribed || !isLoggedIn) {
        alert.success(
          <Alert
            body={t(
              alreadySubscribed
                ? 'NewsletterView_email_already_subscribed_no_reward_received'
                : 'NewsletterView_successfull_subscription_request',
            )}
            title={t('common_success')}
          />,
        );
      } else {
        alert.success(
          <Alert
            body={I18n.t(
              alreadySubscribed
                ? 'NewsletterView_email_already_subscribed_reward_received'
                : 'NewsletterView_successfull_subscription_request_coins',
            )}
            title={I18n.t('common_success')}
          />,
        );
      }
      closePopup();
    } catch (error) {
      hideCaptcha();
      const { message } = parseLootBoyError(error);
      alert.error(
        <Alert
          title={t('common_error')}
          body={message}
        />,
      );
    }
  };

  return (
    <CaptchaOverlay
      dismissCaptcha={hideCaptcha}
      showCaptcha={isCaptchaShown}
      onChange={subscribeToNewsletter}
    >
      <MenuViewHeader title={t('MenuView_newsletter_bc')} />
      <ScrollContainer>
        <section className={styles.container}>
          <h1 className={styles.headline}>
            {t('MenuView_newsletter_headline')}
          </h1>
          <div className={styles.imageContainer}>
            <Image
              alt=""
              src={newsletterHero}
              height={706}
              width={690}
              className={styles.hero}
            />
          </div>
          {isAlreadySubscribed ? (
            <div className={styles.subscribed}>
              <CheckIcon className={styles.checkIcon} />
              <p className={styles.subscribedInfo}>
                {t('MenuView_newsletter_alreadysub')}
              </p>
            </div>
          ) : (
            <Markdown className={styles.sub}>
              {isLoggedIn
                ? t('MenuView_newsletter_sub')
                : t('NewsletterView_description_not_logged_in')}
            </Markdown>
          )}
          <form
            onSubmit={onFormSubmit}
            className={styles.form}
          >
            <Input
              {...register(ENewsletterField.EMAIL)}
              error={errors[ENewsletterField.EMAIL]?.message}
              id={ENewsletterField.EMAIL}
              label=""
              placeholder={newsletterEmail || t('MenuView_email_placeholder')}
              type="email"
            />
            <StyledButton
              disabled={!isValid || isSubmitting}
              type="submit"
              className={styles.submitButton}
              variant={EStyledButtonVariant.BLUE}
            >
              {isAlreadySubscribed
                ? t('MenuView_newsletter_alreadysub_pbutton')
                : t('MenuView_newsletter_pbutton')}
            </StyledButton>
          </form>
        </section>
      </ScrollContainer>
    </CaptchaOverlay>
  );
}
